import { Helmet } from "react-helmet-async";
import { lazy, Suspense, useState, useEffect } from "react";
import HeroSection from "@/components/HeroSection";
import SkipToContent from "@/components/SkipToContent";
import LazySection from "@/components/LazySection";
import { lazyRetry } from "@/lib/lazyRetry";

const Navbar = lazy(() => lazyRetry(() => import("@/components/Navbar")));
const ScrollProgressBar = lazy(() => lazyRetry(() => import("@/components/ScrollProgressBar")));
const BackToTop = lazy(() => lazyRetry(() => import("@/components/BackToTop")));

const FloatingOrbs = lazy(() => lazyRetry(() => import("@/components/FloatingOrbs")));
const Floating3DShapes = lazy(() => lazyRetry(() => import("@/components/Floating3DShapes")));
const AnimatedDivider = lazy(() => lazyRetry(() => import("@/components/AnimatedDivider")));

// Lazy load below-the-fold sections
const SectionTransition = lazy(() => lazyRetry(() => import("@/components/SectionTransition")));
const TrustStrip = lazy(() => lazyRetry(() => import("@/components/TrustStrip")));
const PositioningSection = lazy(() => lazyRetry(() => import("@/components/PositioningSection")));
const PrimarySegmentsSection = lazy(() => lazyRetry(() => import("@/components/PrimarySegmentsSection")));
const ProductizedOffersSection = lazy(() => lazyRetry(() => import("@/components/ProductizedOffersSection")));
const DeliveryModelSection = lazy(() => lazyRetry(() => import("@/components/DeliveryModelSection")));
const BusinessOutcomesSection = lazy(() => lazyRetry(() => import("@/components/BusinessOutcomesSection")));
const TrustBand = lazy(() => lazyRetry(() => import("@/components/TrustBand")));
const PricingPreviewSection = lazy(() => lazyRetry(() => import("@/components/PricingPreviewSection")));
const CaseStudyScaffoldSection = lazy(() => lazyRetry(() => import("@/components/CaseStudyScaffoldSection")));
const DiscordPortfolio = lazy(() => lazyRetry(() => import("@/components/DiscordPortfolio")));
const TestimonialsSection = lazy(() => lazyRetry(() => import("@/components/TestimonialsSection")));
const CTASection = lazy(() => lazyRetry(() => import("@/components/CTASection")));
const FAQSection = lazy(() => lazyRetry(() => import("@/components/FAQSection")));
const ContactSection = lazy(() => lazyRetry(() => import("@/components/ContactSection")));
const Footer = lazy(() => lazyRetry(() => import("@/components/Footer")));
const Preview3DSection = lazy(() => lazyRetry(() => import("@/components/Preview3DSection")));
const HorizontalScrollGallery = lazy(() => lazyRetry(() => import("@/components/HorizontalScrollGallery")));

function useIdleDefer(delay = 2000): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(() => setReady(true), { timeout: delay });
      return () => (window as any).cancelIdleCallback(id);
    } else {
      const t = setTimeout(() => setReady(true), delay);
      return () => clearTimeout(t);
    }
  }, [delay]);
  return ready;
}

const Index = () => {
  const orbsReady = useIdleDefer(2000);

  return (
    <>
      <Helmet>
        <title>FearlessXCR7 — Community Infrastructure for Brands & Institutions</title>
        <meta
          name="description"
          content="FearlessXCR7 designs Discord-based engagement systems, retention engines, and monetizable communities for real estate, education, and modern brands."
        />
        <meta
          name="keywords"
          content="community infrastructure, Discord ecosystem, real estate community, education community, retention systems, FearlessXCR7, Nashik, India"
        />
        <meta property="og:title" content="FearlessXCR7 — Community Infrastructure Company" />
        <meta
          property="og:description"
          content="We don't build Discord servers. We build digital ecosystems engineered for engagement, retention, and recurring revenue."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fearlessxcr7.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.fearlessxcr7.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What services does FearlessXCR7 offer?", "acceptedAnswer": { "@type": "Answer", "text": "We offer Discord server build, manage & scaling, community building, custom bot development, security audits, automation systems, and end-to-end digital infrastructure for businesses and communities." } },
            { "@type": "Question", "name": "How long does it take to set up a Discord server?", "acceptedAnswer": { "@type": "Answer", "text": "Basic server setup takes 2-3 days; a full-featured community server with custom bots, roles, and automation takes 1-2 weeks with thorough testing before delivery." } },
            { "@type": "Question", "name": "What is Project X?", "acceptedAnswer": { "@type": "Answer", "text": "Project X is our flagship initiative — a digital ecosystem for trading communities combining advanced Discord infrastructure, AI tools, and scalable systems for long-term growth." } },
            { "@type": "Question", "name": "Do you offer ongoing support after project completion?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — dedicated support packages cover maintenance, updates, moderation assistance, and 24/7 emergency response for critical issues." } },
            { "@type": "Question", "name": "How do I get started with FearlessXCR7?", "acceptedAnswer": { "@type": "Answer", "text": "Reach out via our contact form or Discord community. We schedule a consultation, send a tailored proposal, and begin building your solution." } },
            { "@type": "Question", "name": "What are your pricing options?", "acceptedAnswer": { "@type": "Answer", "text": "Pricing is tailored per project scope — flexible packages from basic setups to enterprise retainers. Contact us for a custom quote." } }
          ]
        })}</script>
      </Helmet>

      <SkipToContent />

      <Suspense fallback={null}>
        <ScrollProgressBar />
      </Suspense>

      {orbsReady && (
        <Suspense fallback={null}>
          <FloatingOrbs />
          <Floating3DShapes />
        </Suspense>
      )}

      <main id="main-content" tabIndex={-1} className="relative z-10 outline-none">
        <Suspense fallback={null}>
          <Navbar />
        </Suspense>

        {/* 1. Hero */}
        <HeroSection />

        {/* 2. Trust Strip — sleek credibility bar */}
        <Suspense fallback={null}>
          <TrustStrip />
        </Suspense>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>

        {/* 3. What we actually are — Positioning */}
        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <PositioningSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>

        {/* 4. Primary Service Pillars */}
        <div id="solutions">
          <LazySection>
            <SectionTransition delay={0.1}>
              <ProductizedOffersSection />
            </SectionTransition>
          </LazySection>
        </div>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* 5. Primary Sectors — Real Estate + Education */}
        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <PrimarySegmentsSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>

        {/* 6. Why FearlessXCR7 — Delivery Model */}
        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <DeliveryModelSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* 6.5 Interactive 3D Preview */}
        <LazySection>
          <Preview3DSection />
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>

        {/* 7. Business Outcomes — Horizontal Capability Gallery */}
        <LazySection>
          <HorizontalScrollGallery />
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* 7.5 Business Outcomes */}
        <LazySection>
          <SectionTransition delay={0.1}>
            <BusinessOutcomesSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* 8. Pricing Preview */}
        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <PricingPreviewSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>

        {/* 9. Case Study scaffold */}
        <LazySection>
          <SectionTransition delay={0.1}>
            <CaseStudyScaffoldSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>

        {/* Trust Band — extended */}
        <LazySection>
          <SectionTransition delay={0.1}>
            <TrustBand />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* Supporting: Discord portfolio + Testimonials */}
        <LazySection>
          <SectionTransition delay={0.1}>
            <DiscordPortfolio />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="wave" />
        </Suspense>

        <LazySection>
          <SectionTransition delay={0.1} direction="left">
            <TestimonialsSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        {/* 10. Final CTA */}
        <LazySection>
          <SectionTransition delay={0.1}>
            <CTASection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="beam" />
        </Suspense>

        <LazySection>
          <SectionTransition delay={0.1}>
            <FAQSection />
          </SectionTransition>
        </LazySection>

        <Suspense fallback={null}>
          <AnimatedDivider variant="pulse" />
        </Suspense>

        <LazySection>
          <SectionTransition delay={0.1} direction="right">
            <ContactSection />
          </SectionTransition>
        </LazySection>

        <LazySection>
          <Footer />
        </LazySection>

        <Suspense fallback={null}>
          <BackToTop />
        </Suspense>
      </main>
    </>
  );
};

export default Index;
